import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Banknote, CreditCard, Shield, Building, User, CloudOff } from 'lucide-react';
import { IdentityBadge } from '../../care/IdentityBadge';
import { fmtCurrency } from '../../lib/localeUtils';

/** Customer category, payment method, payer details and patient autocomplete. */
export const POSCustomerFields = ({ customer, payment, partners, total }) => (
  <>
    <div className="space-y-2">
      <Label>Customer Category</Label>
      <Select
        value={customer.category}
        onValueChange={(v) => {
          customer.setCategory(v);
          partners.setSelectedInsurance(null);
          partners.setSelectedCorporate(null);
        }}
        data-testid="customer-category-select"
      >
        <SelectTrigger data-testid="customer-category-trigger"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="cash"><div className="flex items-center gap-2"><Banknote className="h-3.5 w-3.5" /> Cash Customer</div></SelectItem>
          <SelectItem value="insurance"><div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Insurance</div></SelectItem>
          <SelectItem value="corporate"><div className="flex items-center gap-2"><Building className="h-3.5 w-3.5" /> Corporate</div></SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Payment Method</Label>
      <Select value={payment.method} onValueChange={payment.setMethod} data-testid="payment-method-select">
        <SelectTrigger data-testid="payment-method-trigger"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="cash"><div className="flex items-center gap-2"><Banknote className="h-3.5 w-3.5" /> Cash</div></SelectItem>
          <SelectItem value="card"><div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /> Card</div></SelectItem>
          <SelectItem value="mobile_money"><div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /> Mobile Money</div></SelectItem>
          <SelectItem value="upi"><div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /> UPI</div></SelectItem>
          {customer.category === 'insurance' && (
            <SelectItem value="insurance_claim"><div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Insurance Claim</div></SelectItem>
          )}
          {customer.category === 'corporate' && (
            <SelectItem value="credit"><div className="flex items-center gap-2"><Building className="h-3.5 w-3.5" /> Corporate Credit</div></SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>

    {customer.category === 'insurance' && payment.method === 'insurance_claim' && (
      <div className="space-y-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800" data-testid="insurance-details-section">
        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1"><Shield className="h-3 w-3" /> Insurance Details</p>
        <Select
          value={partners.selectedInsurance?.provider_id || ''}
          onValueChange={(v) => {
            const p = partners.insuranceProviders.find((x) => x.provider_id === v);
            partners.setSelectedInsurance(p);
            partners.setCoveragePercent(p?.coverage_percent || 80);
          }}
        >
          <SelectTrigger data-testid="insurance-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
          <SelectContent>
            {partners.insuranceProviders.map((p) => (
              <SelectItem key={p.provider_id} value={p.provider_id}>{p.name} ({p.coverage_percent}%)</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Policy Number</Label>
            <Input value={partners.policyNumber} onChange={(e) => partners.setPolicyNumber(e.target.value)} placeholder="POL-XXX" className="h-8 text-sm" data-testid="policy-number-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Coverage %</Label>
            <Input type="number" min={0} max={100} value={partners.coveragePercent} onChange={(e) => partners.setCoveragePercent(parseFloat(e.target.value) || 0)} className="h-8 text-sm" data-testid="coverage-input" />
          </div>
        </div>
        {partners.selectedInsurance && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white dark:bg-background p-2 rounded">
              <p className="text-muted-foreground">Claim</p>
              <p className="font-bold text-blue-600">{fmtCurrency(total * partners.coveragePercent / 100)}</p>
            </div>
            <div className="bg-white dark:bg-background p-2 rounded">
              <p className="text-muted-foreground">Copay</p>
              <p className="font-bold">{fmtCurrency(total * (100 - partners.coveragePercent) / 100)}</p>
            </div>
          </div>
        )}
      </div>
    )}

    {customer.category === 'corporate' && (
      <div className="space-y-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
        <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 flex items-center gap-1"><Building className="h-3 w-3" /> Corporate Details</p>
        <Select
          value={partners.selectedCorporate?.client_id || ''}
          onValueChange={(v) => partners.setSelectedCorporate(partners.corporateClients.find((x) => x.client_id === v))}
        >
          <SelectTrigger data-testid="corporate-client-select"><SelectValue placeholder="Select company" /></SelectTrigger>
          <SelectContent>
            {partners.corporateClients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="space-y-1">
          <Label className="text-xs">Employee ID</Label>
          <Input value={partners.employeeId} onChange={(e) => partners.setEmployeeId(e.target.value)} placeholder="EMP-XXX" className="h-8 text-sm" data-testid="employee-id-input" />
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2 relative">
        <Label className="flex items-center gap-1"><User className="h-3 w-3" /> Customer Name</Label>
        <Input
          value={customer.name}
          onChange={(e) => customer.onNameChange(e.target.value)}
          onFocus={() => { if (customer.suggestions.length > 0) customer.setShowSuggestions(true); }}
          onBlur={() => setTimeout(() => customer.setShowSuggestions(false), 200)}
          placeholder="Type to search patients..."
          data-testid="customer-name-input"
          autoComplete="off"
        />
        {customer.selectedPatientId && (
          <span className="absolute right-2 top-[34px] flex items-center gap-1">
            <IdentityBadge patientId={customer.selectedPatientId} compact />
            <Badge variant="outline" className="text-[9px] text-primary border-primary/40">Linked</Badge>
          </span>
        )}
        {customer.showSuggestions && customer.suggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 top-[62px] bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="patient-suggestions">
            {customer.suggestions.map((p) => (
              <button
                key={p.patient_id || p.phone}
                className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between"
                onMouseDown={(e) => { e.preventDefault(); customer.onSelectPatient(p); }}
                data-testid={`patient-suggestion-${p.patient_id || p.phone}`}
              >
                <span className="font-medium truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground ml-2 shrink-0 flex items-center gap-1">
                  {p.phone || 'No phone'}
                  {p.is_cached && <CloudOff className="h-3 w-3" aria-label="From offline cache" />}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>Phone</Label>
        <Input value={customer.phone} onChange={(e) => customer.setPhone(e.target.value)} placeholder="Optional" data-testid="customer-phone-input" />
      </div>
    </div>
  </>
);
